import { useState } from 'react';
import { Plus, Loader2, Tag, FileText, Mail, Star, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { CATEGORY_LABEL } from '@/modules/sales-research/lib';
import { useOffers, useTemplates, useCrmAction, useMailSenders } from './api';
import { Modal, Field, TextInput, Textarea, Select } from './widgets';
import type { Offer, Template } from './types';

export function OffersTemplates() {
  const offersQ = useOffers();
  const templatesQ = useTemplates();
  const [editOffer, setEditOffer] = useState<Partial<Offer> | null>(null);
  const [editTpl, setEditTpl] = useState<Partial<Template> | null>(null);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* offers */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-semibold"><Tag className="h-4 w-4" /> Rate card / offers</h2>
          <Button size="sm" variant="outline" onClick={() => setEditOffer({ vertical: 'corporate', is_published: false })}>
            <Plus className="h-3.5 w-3.5" /> New offer
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">The agent may only quote a <strong>published</strong> offer.</p>
        {(offersQ.data ?? []).map((o) => (
          <Card key={o.id} className="cursor-pointer p-3 hover:border-[#51BFAA]" onClick={() => setEditOffer(o)}>
            <div className="flex items-center gap-2">
              <span className="font-medium">{o.name}</span>
              {o.is_published ? <Badge variant="positive">published</Badge> : <Badge variant="muted">draft</Badge>}
              {o.city && <Badge variant="outline">{o.city}</Badge>}
              {o.category && <Badge variant="outline">{CATEGORY_LABEL[o.category]}</Badge>}
            </div>
            {o.headline && <p className="mt-1 text-xs text-muted-foreground">{o.headline}</p>}
            {o.price_note && <p className="mt-0.5 text-xs font-medium">{o.price_note}</p>}
          </Card>
        ))}
      </section>

      {/* templates */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-semibold"><FileText className="h-4 w-4" /> Email templates</h2>
          <Button size="sm" variant="outline" onClick={() => setEditTpl({ vertical: 'corporate', channel: 'email', stage: 'intro', is_active: true })}>
            <Plus className="h-3.5 w-3.5" /> New template
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Guidance the agent personalizes. Placeholders: {'{company} {outlet} {distance} {cta}'}.</p>
        {(templatesQ.data ?? []).map((t) => (
          <Card key={t.id} className="cursor-pointer p-3 hover:border-[#51BFAA]" onClick={() => setEditTpl(t)}>
            <div className="flex items-center gap-2">
              <span className="font-medium">{t.name}</span>
              <Badge variant="muted">{t.stage}</Badge>
              {!t.is_active && <Badge variant="outline">inactive</Badge>}
            </div>
            {t.subject && <p className="mt-1 text-xs text-muted-foreground">Subject: {t.subject}</p>}
          </Card>
        ))}
      </section>

      {editOffer && <OfferModal offer={editOffer} onClose={() => setEditOffer(null)} />}
      {editTpl && <TemplateModal tpl={editTpl} onClose={() => setEditTpl(null)} />}

      <MailSendersManager />
    </div>
  );
}

function MailSendersManager() {
  const sendersQ = useMailSenders();
  const act = useCrmAction();
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const senders = sendersQ.data ?? [];

  async function add() {
    const a = address.trim().toLowerCase();
    if (!a.includes('@')) return;
    await act.mutateAsync({ action: 'save_sender', address: a, label: label.trim() || null, is_default: senders.length === 0 });
    setAddress(''); setLabel('');
  }
  async function makeDefault(s: any) {
    await act.mutateAsync({ action: 'save_sender', address: s.address, label: s.label, is_active: true, is_default: true });
  }
  async function remove(a: string) {
    await act.mutateAsync({ action: 'delete_sender', address: a });
  }

  return (
    <section className="space-y-3 lg:col-span-2">
      <h2 className="flex items-center gap-2 text-sm font-semibold"><Mail className="h-4 w-4" /> Sending mailboxes</h2>
      <p className="text-xs text-muted-foreground">
        The mailboxes drafts can be created in — you pick one when approving. Each must be a real Google
        Workspace user your service account is authorized to impersonate. The ⭐ default is pre-selected.
      </p>
      <div className="space-y-2">
        {senders.length === 0 && <p className="text-xs text-muted-foreground">No mailboxes yet — add one below.</p>}
        {senders.map((s) => (
          <Card key={s.address} className="flex items-center gap-2 p-2.5 text-sm">
            <button
              title={s.is_default ? 'Default mailbox' : 'Make default'}
              onClick={() => !s.is_default && makeDefault(s)}
              className={s.is_default ? 'text-[#E0A100]' : 'text-muted-foreground hover:text-foreground'}
            >
              <Star className="h-4 w-4" fill={s.is_default ? 'currentColor' : 'none'} />
            </button>
            <span className="font-medium">{s.address}</span>
            {s.label && <span className="text-xs text-muted-foreground">{s.label}</span>}
            {s.is_default && <Badge variant="muted">default</Badge>}
            <button title="Remove" onClick={() => remove(s.address)} className="ml-auto text-muted-foreground hover:text-[#B15599]">
              <Trash2 className="h-4 w-4" />
            </button>
          </Card>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Mailbox address"><TextInput value={address} onChange={(e) => setAddress(e.target.value)} placeholder="sales@flaxitup.com" /></Field>
        <Field label="Label (optional)"><TextInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Mumbai sales" /></Field>
        <Button size="sm" disabled={act.isPending || !address.includes('@')} onClick={add}>
          {act.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add mailbox
        </Button>
      </div>
    </section>
  );
}

function OfferModal({ offer, onClose }: { offer: Partial<Offer>; onClose: () => void }) {
  const act = useCrmAction();
  const [f, setF] = useState<Partial<Offer>>(offer);
  const set = (k: keyof Offer, v: any) => setF((s) => ({ ...s, [k]: v }));

  async function save() {
    await act.mutateAsync({ action: 'save_offer', offer: f });
    onClose();
  }
  return (
    <Modal open onClose={onClose} title={offer.id ? 'Edit offer' : 'New offer'}>
      <div className="space-y-3">
        <Field label="Name"><TextInput value={f.name ?? ''} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Headline"><TextInput value={f.headline ?? ''} onChange={(e) => set('headline', e.target.value)} /></Field>
        <Field label="Body (the facts the agent may state)"><Textarea rows={4} value={f.body ?? ''} onChange={(e) => set('body', e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Price note"><TextInput value={f.price_note ?? ''} onChange={(e) => set('price_note', e.target.value)} /></Field>
          <Field label="Call to action"><TextInput value={f.cta ?? ''} onChange={(e) => set('cta', e.target.value)} /></Field>
          <Field label="City (blank = all)"><TextInput value={f.city ?? ''} onChange={(e) => set('city', e.target.value)} /></Field>
          <Field label="Category (blank = all)">
            <Select value={(f.category as string) ?? ''} onChange={(v) => set('category', v || null)} placeholder="All categories"
              options={Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }))} className="w-full" />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!f.is_published} onChange={(e) => set('is_published', e.target.checked)} />
          Published (agent may use this offer)
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={act.isPending || !f.name || !f.body} onClick={save}>
            {act.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function TemplateModal({ tpl, onClose }: { tpl: Partial<Template>; onClose: () => void }) {
  const act = useCrmAction();
  const [f, setF] = useState<Partial<Template>>(tpl);
  const set = (k: keyof Template, v: any) => setF((s) => ({ ...s, [k]: v }));

  async function save() {
    await act.mutateAsync({ action: 'save_template', template: f });
    onClose();
  }
  return (
    <Modal open onClose={onClose} title={tpl.id ? 'Edit template' : 'New template'}>
      <div className="space-y-3">
        <Field label="Name"><TextInput value={f.name ?? ''} onChange={(e) => set('name', e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Stage">
            <Select value={f.stage ?? 'intro'} onChange={(v) => set('stage', v)} className="w-full"
              options={[{ value: 'intro', label: 'Intro' }, { value: 'followup', label: 'Follow-up' }]} />
          </Field>
          <Field label="Channel">
            <Select value={f.channel ?? 'email'} onChange={(v) => set('channel', v)} className="w-full"
              options={[{ value: 'email', label: 'Email' }, { value: 'whatsapp', label: 'WhatsApp' }]} />
          </Field>
        </div>
        <Field label="Subject"><TextInput value={f.subject ?? ''} onChange={(e) => set('subject', e.target.value)} /></Field>
        <Field label="Body (guidance for the agent)"><Textarea rows={5} value={f.body ?? ''} onChange={(e) => set('body', e.target.value)} /></Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.is_active !== false} onChange={(e) => set('is_active', e.target.checked)} />
          Active
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={act.isPending || !f.name || !f.body} onClick={save}>
            {act.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}
